import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MenuModel } from '../models/menu.model';

export interface TelephonyManifestPage {
  id: string;
  title: string;
  route: string;
  frame: string;
  icon: string;
  category: string;
  order: number;
  visibleTo?: string[];
  requiredCapabilities?: string[];
  enabled: boolean;
}

export interface TelephonyPluginManifest {
  pluginId: string;
  name: string;
  version?: string;
  hostSystem: string;
  mountPath: string;
  apiBase: string;
  configUrl: string;
  healthUrl?: string;
  adapterType: string;
  capabilities?: string[];
  api?: Record<string, string>;
  exports?: Record<string, string>;
  adapterContract?: Record<string, string | string[]>;
  labels: {
    agent: string;
    caller: string;
    businessRecord: string;
    internalPeer: string;
  };
  pages: TelephonyManifestPage[];
}

const TOKEN_KEY = 'auth-token';
const MANIFEST_URL = `${environment.apiHost}/api/v1/telephony/manifest`;

@Injectable({ providedIn: 'root' })
export class TelephonyPluginManifestService {
  private readonly manifestSubject = new BehaviorSubject<TelephonyPluginManifest | null>(null);
  readonly manifest$ = this.manifestSubject.asObservable();

  get value(): TelephonyPluginManifest | null {
    return this.manifestSubject.value;
  }

  async load(): Promise<TelephonyPluginManifest | null> {
    try {
      const token = localStorage.getItem(TOKEN_KEY) ?? '';
      const res = await fetch(MANIFEST_URL, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return this.manifestSubject.value;
      const json: any = await res.json();
      const manifest = (json?.data ?? json) as TelephonyPluginManifest;
      this.manifestSubject.next(manifest);
      return manifest;
    } catch {
      return this.manifestSubject.value;
    }
  }

  toMenu(manifest: TelephonyPluginManifest, userRole?: string): MenuModel {
    const normalizedRole = this.normalizeRole(userRole);
    return {
      title: manifest.name || 'Contact Center',
      route: '',
      frame: 'TELEPHONY_CONTACT_CENTER',
      order: 15,
      icon: 'headset_mic',
      subMenu: manifest.pages
        .slice()
        .filter(page => page.enabled !== false)
        .filter(page => this.isVisible(page, normalizedRole))
        .sort((a, b) => a.order - b.order)
        .map(page => ({
          title: page.title,
          route: page.route,
          frame: page.frame,
          order: page.order,
          icon: page.icon ?? '',
        })),
    };
  }

  private isVisible(page: TelephonyManifestPage, normalizedRole: string): boolean {
    if (!page.visibleTo?.length) return true;
    if (!normalizedRole) return true;
    return page.visibleTo.includes(normalizedRole);
  }

  private normalizeRole(role?: string): string {
    const value = (role ?? '').toLowerCase();
    if (['admin', 'super_admin'].includes(value)) return 'admin';
    if (['manager', 'supervisor'].includes(value)) return 'supervisor';
    if (['internal_user', 'csr', 'agent'].includes(value)) return 'agent';
    return value;
  }
}
